import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { get, isEqual, isNil, map, isEmpty } from 'lodash';
import turf from '@turf/turf';

import { VIDEO_COORDINATE_DATA_TYPES } from '../../../api/Videos/Videos';

import { featurePoint2LatLng, featureGetZoom } from '../../../modules/geojson-utilities';

import LeafletMap from '../../components/LeafletMap';

import * as videoOps from '../../../api/Videos/video-operations';

let addDrawEventListeners;
let addOnlineBaseMap;
let createGeometryDrawControls;
let customCollapseButton;
let customHomeButton;
let customMapsAttribution;
let customScale;
let customZoom;
let drawVideoGeometry;
let Geocoding;
let showGeometryEditControls;
let drawMapLayers;

class MapContainerVideoEditor extends Component {
  state = {
    clientModulesReady: false,
    isLeafletMapLoaded: get(this, 'videoEditorMapComponent.current.state.isMapLoaded', false),
    isVideoEditorMapLoaded: false
  };

  videoEditorMapComponent = React.createRef();

  videoEditorMap = null;

  lineDrawControl = null;

  lineEditControl = null;

  pointDrawControl = null;

  pointEditControl = null;

  geometryLayer = null;

  componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) {
      this.loadClientModules();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      coordinatesDataType,
      defineLineActive,
      definePointActive,
      latLng0,
      mapLocation,
      track
    } = this.props;
    const { clientModulesReady, isLeafletMapLoaded, isVideoEditorMapLoaded } = this.state;
    const {
      defineLineActive: prevDefineLineActive,
      definePointActive: prevDefinePointActive,
      latLng0: prevLatLng0,
      mapLocation: prevMapLocation,
      track: prevTrack
    } = prevProps;

    if (clientModulesReady && isLeafletMapLoaded && !isVideoEditorMapLoaded) {
      this.initializeMap();
    }

    if (isVideoEditorMapLoaded) {
      if (!isEqual(mapLocation, prevMapLocation)) {
        this.videoEditorMap.setView(featurePoint2LatLng(mapLocation));
      }

      if (!isEqual(track, prevTrack) || !isEqual(latLng0, prevLatLng0)) {
        drawVideoGeometry({
          leafletMap: this.videoEditorMap,
          coordinatesDataType,
          latLng0,
          track,
          drawnItems: this.geometryLayer,
          isUpdate: true
        });
      }

      if (defineLineActive !== prevDefineLineActive) {
        showGeometryEditControls({
          leafletMap: this.videoEditorMap,
          defineGeometryActive: defineLineActive,
          drawControl: this.lineDrawControl,
          editControl: this.lineEditControl,
          geometryLayer: this.geometryLayer
        });
      }

      if (definePointActive !== prevDefinePointActive) {
        showGeometryEditControls({
          leafletMap: this.videoEditorMap,
          defineGeometryActive: definePointActive,
          drawControl: this.pointDrawControl,
          editControl: this.pointEditControl,
          geometryLayer: this.geometryLayer
        });
      }
    }
  }

  getCurrentMapState = () => {
    const { coordinatesDataType, defineLineActive, definePointActive } = this.props;

    let drawControl = null;
    let editControl = null;
    if (coordinatesDataType !== VIDEO_COORDINATE_DATA_TYPES.POINT) {
      drawControl = this.lineDrawControl;
      editControl = this.lineEditControl;
    } else if (coordinatesDataType === VIDEO_COORDINATE_DATA_TYPES.POINT) {
      drawControl = this.pointDrawControl;
      editControl = this.pointEditControl;
    }

    return {
      defineGeometryActive: defineLineActive || definePointActive,
      drawControl,
      editControl,
      geometryLayer: this.geometryLayer,
      itemType: 'video',
      leafletMap: this.videoEditorMap,
      setVideoGeometry: this.setVideoGeometry
    };
  };

  initializeMap = async () => {
    const { coordinatesDataType, latLng0, mapLocation, track, updatedVideo } = this.props;
    this.videoEditorMap = get(this, 'videoEditorMapComponent.current.leafletMap');
    this.videoEditorMap.setView(featurePoint2LatLng(mapLocation), featureGetZoom(mapLocation));

    if (this.videoEditorMap.zoomControl) {
      this.videoEditorMap.removeControl(this.videoEditorMap.zoomControl);
    }
    if (this.videoEditorMap.collapseButton) {
      this.videoEditorMap.removeControl(this.videoEditorMap.collapseButton);
    }
    if (this.videoEditorMap.attributionControl) {
      this.videoEditorMap.removeControl(this.videoEditorMap.attributionControl);
    }

    const collapseButton = customCollapseButton(this.videoEditorMap, false);
    customZoom(this.videoEditorMap);
    customMapsAttribution(this.videoEditorMap);
    customHomeButton(this.videoEditorMap, mapLocation);
    customScale(this.videoEditorMap);

    const searchControl = Geocoding.geosearch({ useMapBounds: false });
    this.videoEditorMap.addControl(searchControl);
    this.videoEditorMap.searchControl = searchControl;

    const { baseMaps, layersMaps } = videoOps.getGeomotionMap(updatedVideo);
    const base = !isEmpty(baseMaps) ? baseMaps : [];
    const layer = !isEmpty(layersMaps) ? layersMaps : [];

    if (!isEmpty(baseMaps)) {
      await drawMapLayers({
        layers: layer,
        baseMaps: base,
        popupProp: false,
        leafletMap: this.videoEditorMap,
        layersToDisplay: layer,
        baseMapsToDisplay: base,
        isUpdate: false,
        baseMapsTransparency: [],
        layersTransparency: [],
        baseMapsZoom: [],
        layersZoom: []
      });
    } else addOnlineBaseMap(this.videoEditorMap);

    this.geometryLayer = drawVideoGeometry({
      leafletMap: this.videoEditorMap,
      coordinatesDataType,
      latLng0,
      track
    });

    const lineControls = createGeometryDrawControls({
      item: { coordinatesDataType: VIDEO_COORDINATE_DATA_TYPES.LINE },
      drawnItems: this.geometryLayer
    });
    this.lineEditControl = lineControls.drawControlEdit;
    this.lineDrawControl = lineControls.drawControlFull;

    const pointControls = createGeometryDrawControls({
      item: { coordinatesDataType: VIDEO_COORDINATE_DATA_TYPES.POINT },
      drawnItems: this.geometryLayer
    });
    this.pointEditControl = pointControls.drawControlEdit;
    this.pointDrawControl = pointControls.drawControlFull;

    addDrawEventListeners({
      limitation: false,
      leafletMap: this.videoEditorMap,
      getCurrentMapState: this.getCurrentMapState
    });

    collapseButton.expand();

    this.setState({ isVideoEditorMapLoaded: true });
  };

  setVideoGeometry = (featureFromLayer, isCreateGeometry = false) => {
    const {
      defineLineActive,
      definePointActive,
      changeVideoEditorStateProperty,
      toggleButtonSwitch
    } = this.props;

    let newFeatureArray = [];
    let newLatLng0 = [];
    let newTrack = {};

    if (defineLineActive && !isNil(featureFromLayer)) {
      const newTrackCoordinates = turf.coordAll(featureFromLayer);
      newFeatureArray = map(newTrackCoordinates, (currentCoord, index, array) => {
        const properties = {
          distanceTravelled: 0,
          isControlPoint: index === 0 || index === array.length - 1,
          timeUS: 0
        };
        return turf.point(currentCoord, properties);
      });
      [newLatLng0] = turf.getCoords(featureFromLayer);
      newTrack = featureFromLayer;
    }

    if (definePointActive && !isNil(featureFromLayer)) {
      newLatLng0 = turf.getCoord(featureFromLayer);
      newFeatureArray = [
        turf.point(newLatLng0, { distanceTravelled: 0, isControlPoint: false, timeUS: 0 })
      ];
    }

    changeVideoEditorStateProperty('controlPoints', {});
    changeVideoEditorStateProperty('featureArray', newFeatureArray);
    changeVideoEditorStateProperty('latLng0', newLatLng0);
    changeVideoEditorStateProperty('track', newTrack);
    if (isCreateGeometry) toggleButtonSwitch();
  };

  updateMapState = newState => {
    this.setState({ isLeafletMapLoaded: newState });
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      Geocoding = await import('esri-leaflet-geocoder');
      ({
        addDrawEventListeners,
        addOnlineBaseMap,
        createGeometryDrawControls,
        customCollapseButton,
        customHomeButton,
        customMapsAttribution,
        customScale,
        customZoom,
        drawVideoGeometry,
        showGeometryEditControls,
        drawMapLayers
      } = await import('../../../modules/leaflet-operations'));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { deployEnv } = this.props;
    return (
      <LeafletMap
        mapId="videoEditorMap"
        ref={this.videoEditorMapComponent}
        updateMapState={this.updateMapState}
        headerType="8"
        deployEnv={deployEnv}
      />
    );
  }
}

MapContainerVideoEditor.propTypes = {
  coordinatesDataType: PropTypes.string.isRequired,
  defineLineActive: PropTypes.bool.isRequired,
  definePointActive: PropTypes.bool.isRequired,
  deployEnv: PropTypes.string.isRequired,
  latLng0: PropTypes.array.isRequired,
  mapLocation: PropTypes.object.isRequired,
  track: PropTypes.object.isRequired,
  changeVideoEditorStateProperty: PropTypes.func.isRequired,
  toggleButtonSwitch: PropTypes.func.isRequired,
  updatedVideo: PropTypes.object.isRequired
};

export default MapContainerVideoEditor;
